import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import PageHeader from '../components/PageHeader';
import {
    AreaChart, Area, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer,
} from 'recharts';
import { TrendingUp, FileText, Users, AlertTriangle } from 'lucide-react';

const fmt  = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);
const fmt2 = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(n || 0);

const TEAL   = '#3BC6B4';
const NAVY   = '#0B3C5D';
const INDIGO = '#6366F1';
const AMBER  = '#F59E0B';

function Section({ title, children, icon: Icon, action }) {
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 text-[13.5px] font-semibold text-[color:var(--bm-text)]">
                    {Icon && <Icon size={16} className="text-[color:var(--bm-primary)]" />} {title}
                </div>
                {action}
            </div>
            {children}
        </div>
    );
}

function Stat({ label, value, sub, tint = 'text-[color:var(--bm-text)]' }) {
    return (
        <div className="bg-[color:var(--bm-bg-mute)] rounded-lg px-4 py-3">
            <div className="text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">{label}</div>
            <div className={`font-display text-[20px] font-semibold mt-1 leading-none ${tint}`}>{value}</div>
            {sub && <div className="text-[11.5px] text-[color:var(--bm-text-muted)] mt-0.5">{sub}</div>}
        </div>
    );
}

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white border border-[color:var(--bm-border)] rounded-lg shadow-lg px-3 py-2.5 text-[12.5px]">
            <div className="font-semibold text-[color:var(--bm-text)] mb-1">{label}</div>
            {payload.map(p => (
                <div key={p.name} className="flex items-center gap-2">
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, display: 'inline-block' }} />
                    <span className="text-[color:var(--bm-text-muted)] capitalize">{p.name}</span>
                    <span className="font-medium text-[color:var(--bm-text)] ml-auto pl-4">{fmt(p.value)}</span>
                </div>
            ))}
        </div>
    );
};

export default function Reports() {
    const [trend, setTrend] = useState(null);
    const [funnel, setFunnel] = useState(null);
    const [topClients, setTopClients] = useState(null);
    const [aged, setAged] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([
            api.get('/reports/revenue-trend'),
            api.get('/reports/funnel'),
            api.get('/reports/top-clients'),
            api.get('/aged-debtors'),
        ]).then(([t, f, tc, ad]) => {
            setTrend(t.data);
            setFunnel(f.data);
            setTopClients(tc.data);
            setAged(ad.data);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="p-8 text-[color:var(--bm-text-muted)]">Loading reports…</div>;

    const trendMax = trend ? Math.max(...trend.months.map(m => Math.max(m.invoiced, m.collected)), 1) : 1;
    const funnelMax = funnel ? Math.max(...funnel.stages.map(s => s.count), 1) : 1;

    return (
        <div data-testid="reports-page" className="bm-fade-in">
            <PageHeader
                title="Reports"
                description="Revenue trends, proposal conversion, aged debtors and top clients."
            />

            <div className="px-8 py-7 space-y-7">
                {/* ── Revenue trend ── */}
                {trend && (
                    <Section title="Revenue trend — last 12 months" icon={TrendingUp}
                        action={
                            <div className="flex items-center gap-3 text-[12px] text-[color:var(--bm-text-muted)]">
                                <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: 2, background: INDIGO, display: 'inline-block' }} /> Invoiced</span>
                                <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: 2, background: TEAL, display: 'inline-block' }} /> Collected</span>
                            </div>
                        }>
                        {/* Stat row */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                            <Stat label="Total invoiced (12mo)"  value={fmt(trend.months.reduce((s, m) => s + m.invoiced,  0))} />
                            <Stat label="Total collected (12mo)" value={fmt(trend.months.reduce((s, m) => s + m.collected, 0))} tint="text-[color:var(--bm-accent-600)]" />
                            <Stat label="Setup invoiced (12mo)"  value={fmt(trend.months.reduce((s, m) => s + m.setup,     0))} />
                            <Stat label="SaaS invoiced (12mo)"   value={fmt(trend.months.reduce((s, m) => s + m.saas,      0))} />
                        </div>
                        <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={trend.months} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
                                <defs>
                                    <linearGradient id="gInvoiced" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%"  stopColor={INDIGO} stopOpacity={0.15} />
                                        <stop offset="95%" stopColor={INDIGO} stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="gCollected" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%"  stopColor={TEAL} stopOpacity={0.2} />
                                        <stop offset="95%" stopColor={TEAL} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E3E8ED" />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#8A95A3' }} axisLine={false} tickLine={false} />
                                <YAxis tickFormatter={v => `£${v >= 1000 ? (v/1000).toFixed(0)+'k' : v}`} tick={{ fontSize: 11, fill: '#8A95A3' }} axisLine={false} tickLine={false} width={55} />
                                <Tooltip content={<CustomTooltip />} />
                                <Area type="monotone" dataKey="invoiced"  stroke={INDIGO} strokeWidth={2} fill="url(#gInvoiced)"  name="invoiced" />
                                <Area type="monotone" dataKey="collected" stroke={TEAL}   strokeWidth={2} fill="url(#gCollected)" name="collected" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Section>
                )}

                {/* ── Funnel + Top clients ── */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {/* Proposal funnel */}
                    {funnel && (
                        <Section title="Proposal conversion funnel" icon={FileText}>
                            <div className="grid grid-cols-3 gap-3 mb-5">
                                <Stat label="Total"       value={funnel.total}       />
                                <Stat label="In progress" value={funnel.in_progress} />
                                <Stat label="Win rate"    value={`${funnel.win_rate}%`} tint="text-emerald-600" sub={`${funnel.won} won · ${funnel.lost} lost`} />
                            </div>
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={funnel.stages} layout="vertical" margin={{ top: 0, right: 10, bottom: 0, left: 10 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E3E8ED" horizontal={false} />
                                    <XAxis type="number" domain={[0, funnelMax]} tick={{ fontSize: 11, fill: '#8A95A3' }} axisLine={false} tickLine={false} />
                                    <YAxis type="category" dataKey="label" tick={{ fontSize: 12, fill: '#4A5568' }} axisLine={false} tickLine={false} width={85} />
                                    <Tooltip formatter={(v) => [v, 'proposals']} contentStyle={{ fontSize: 12 }} />
                                    <Bar dataKey="count" fill={NAVY} radius={[0, 4, 4, 0]} barSize={22} name="proposals" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Section>
                    )}

                    {/* Top clients */}
                    {topClients && (
                        <Section title="Top clients by revenue" icon={Users}
                            action={<button onClick={() => navigate('/clients')} className="text-[12px] text-[color:var(--bm-primary)] hover:underline">View all</button>}>
                            <table className="w-full text-[12.5px]">
                                <thead className="text-[color:var(--bm-text-muted)] border-b border-[color:var(--bm-border)]">
                                    <tr>
                                        <th className="text-left py-2 font-medium">Client</th>
                                        <th className="text-right py-2 font-medium">Invoiced</th>
                                        <th className="text-right py-2 font-medium">Collected</th>
                                        <th className="text-right py-2 font-medium">Outstanding</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topClients.clients.map((c, i) => (
                                        <tr key={c.client_id} onClick={() => navigate(`/clients/${c.client_id}`)}
                                            className="border-b border-[color:var(--bm-border)] last:border-0 hover:bg-[color:var(--bm-bg-mute)] cursor-pointer"
                                            data-testid={`top-client-${i}`}>
                                            <td className="py-2.5 font-medium text-[color:var(--bm-text)]">{c.client_name}</td>
                                            <td className="py-2.5 text-right text-[color:var(--bm-text-muted)]">{fmt2(c.total_invoiced)}</td>
                                            <td className="py-2.5 text-right font-medium text-[color:var(--bm-accent-600)]">{fmt2(c.total_collected)}</td>
                                            <td className="py-2.5 text-right font-medium text-[color:var(--bm-text)]">{c.outstanding > 0 ? fmt2(c.outstanding) : '—'}</td>
                                        </tr>
                                    ))}
                                    {topClients.clients.length === 0 && (
                                        <tr><td colSpan={4} className="py-8 text-center text-[color:var(--bm-text-muted)]">No invoice data yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </Section>
                    )}
                </div>

                {/* ── Aged debtors ── */}
                {aged && (
                    <Section title="Aged debtors" icon={AlertTriangle}>
                        {/* Bucket totals */}
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                            {[
                                { label: 'Current',   key: 'current',  tint: 'text-emerald-600' },
                                { label: '1–30 days', key: 'd1_30',    tint: 'text-amber-600' },
                                { label: '31–60 days',key: 'd31_60',   tint: 'text-orange-600' },
                                { label: '61–90 days',key: 'd61_90',   tint: 'text-red-500' },
                                { label: '90+ days',  key: 'd90_plus', tint: 'text-red-700' },
                            ].map(b => (
                                <Stat key={b.key} label={b.label}
                                      value={aged.totals[b.key] > 0 ? fmt2(aged.totals[b.key]) : '—'}
                                      tint={aged.totals[b.key] > 0 ? b.tint : 'text-[color:var(--bm-text-muted)]'} />
                            ))}
                        </div>

                        {/* Per-client breakdown */}
                        {aged.by_client.length > 0 && (
                            <table className="w-full text-[12.5px]">
                                <thead className="text-[color:var(--bm-text-muted)] border-b border-[color:var(--bm-border)]">
                                    <tr>
                                        <th className="text-left py-2 font-medium">Client</th>
                                        <th className="text-right py-2 font-medium">Current</th>
                                        <th className="text-right py-2 font-medium">1–30d</th>
                                        <th className="text-right py-2 font-medium">31–60d</th>
                                        <th className="text-right py-2 font-medium">61–90d</th>
                                        <th className="text-right py-2 font-medium">90+d</th>
                                        <th className="text-right py-2 font-medium">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {aged.by_client.map((c, i) => (
                                        <tr key={i} onClick={() => navigate(`/clients/${c.client_id}`)}
                                            className="border-b border-[color:var(--bm-border)] last:border-0 hover:bg-[color:var(--bm-bg-mute)] cursor-pointer">
                                            <td className="py-2.5 font-medium text-[color:var(--bm-text)]">{c.client_name}</td>
                                            {['current','d1_30','d31_60','d61_90','d90_plus'].map(k => (
                                                <td key={k} className={`py-2.5 text-right ${c[k] > 0 ? 'font-medium text-[color:var(--bm-text)]' : 'text-[color:var(--bm-text-subtle)]'}`}>
                                                    {c[k] > 0 ? fmt2(c[k]) : '—'}
                                                </td>
                                            ))}
                                            <td className="py-2.5 text-right font-semibold text-[color:var(--bm-text)]">{fmt2(c.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {aged.by_client.length === 0 && (
                            <div className="text-[13px] text-[color:var(--bm-text-muted)] py-6 text-center">No outstanding invoices.</div>
                        )}
                    </Section>
                )}
            </div>
        </div>
    );
}
